import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import type { DraggableData, DraggableEvent } from 'react-draggable'
import Draggable from 'react-draggable'

import { Checkbox, Modal, Table, Tag, Tooltip } from 'antd'
import _ from 'lodash'
import moment from 'moment'
import styled from 'styled-components'
import * as Icons from '@ant-design/icons'

import { COUNTRY } from '@qflow/constants'
import { colours, sizes } from '@qflow/theme'

import { ColumnsType } from 'antd/lib/table/interface'

import { Queries } from 'admin/lib/Api'
import { Select } from 'admin/lib/Forms'
import {
  CONFIDENCE_AND_VALIDATION_TYPES,
  CONFIDENCE_BASED_TYPE,
  ConfidenceAndValidationsType,
  ValidationType
} from 'admin/lib/Forms/ConfidenceAndValidationMappings'

import { Minimize } from 'admin/lib/Icons/Minimize'
import { getCertStatus } from 'admin/lib/Certificates/CertificateExpiry'
import { Matchers } from 'admin/lib/Records'
import { DeliveryProductCertOptions } from 'admin/lib/Records/constants'
import { useGlobalShortcut } from 'admin/lib/shortcuts'
import * as Validation from 'admin/lib/SimpleDataValidation'

import { ENRICHED, MAX_MEASUREMENT_SIZES } from './constants'
import { isExtendedPGList } from './rawEnrichedHelpers'
import { getLocaleFromCountry } from 'admin/lib/Functions/timeZoneHelper'
import { TranslationTextForTooltip } from 'admin/lib/Translations/TranslationTextForTooltip'

type RouteParams = {
  accountId: string
  projectId: string
  moduleName: string
}

export const LineItemSummary = ({
  items,
  rawItems,
  isOpen,
  onClose,
  projectCountry,
  projectTimeZone,
  isImperial,
  dataView,
  setDataView,
  openAll,
  arrivalDate = null,
  exitDate = null,
  enrichedDisabled = false,
  enrichedConfidenceAndValidation = null
}: {
  items: any[]
  rawItems: any[]
  isOpen: boolean
  onClose: () => void
  projectCountry: string
  projectTimeZone: string
  isImperial: boolean
  dataView: string
  setDataView: (view: string) => void
  openAll?: () => void
  arrivalDate?: string | null
  exitDate?: string | null
  enrichedDisabled?: boolean
  enrichedConfidenceAndValidation?: Record<
    string,
    ConfidenceAndValidationsType
  > | null
}) => {
  const [disabled, setDisabled] = useState(true)
  const [bounds, setBounds] = useState({ left: 0, top: 0, bottom: 0, right: 0 })
  const [minimized, setMinimized] = useState(false)
  const draggleRef = useRef<HTMLDivElement>(null)

  // On Summary box open, set all items to expanded for easier access to fields.
  useEffect(() => {
    openAll?.()
  }, [openAll])

  const onStart = (_event: DraggableEvent, uiData: DraggableData) => {
    const { clientWidth, clientHeight } = window.document.documentElement
    const targetRect = draggleRef.current?.getBoundingClientRect()
    if (!targetRect) {
      return
    }
    setBounds({
      left: -targetRect.left + uiData.x,
      right: clientWidth - (targetRect.right - uiData.x),
      top: -targetRect.top + uiData.y,
      bottom: clientHeight - (targetRect.bottom - uiData.y)
    })
  }

  const { accountId, projectId, moduleName } = useParams<RouteParams>()

  const allProductGroups = Queries.useProductGroups({
    accountId,
    projectId
  })

  const projectLocale = useMemo(
    () => getLocaleFromCountry(projectCountry),
    [projectCountry]
  )

  const UoM = useMemo(
    () => ({
      Millimeter: 'mm',
      Meter: 'm',
      Foot: 'ft',
      Inch: 'in',
      CubicMeter: 'm3',
      Liter: 'l',
      ImperialGallon: 'gal',
      Tonne: 't',
      Kilogram: 'kg',
      Pound: 'lbs',
      ShortTon: 'ton',
      CubicYard: 'cu yd',
      UsGallon: 'gal',
      CubicFoot: 'cf',
      BoardFoot: 'bf'
    }),
    []
  )

  const UoMOptions = useMemo(
    () => ({
      heightUoM: [
        { value: 'Millimeter', label: UoM['Millimeter'] },
        { value: 'Meter', label: UoM['Meter'] },
        { value: 'Foot', label: UoM['Foot'] },
        { value: 'Inch', label: UoM['Inch'] }
      ],
      widthUoM: [
        { value: 'Millimeter', label: UoM['Millimeter'] },
        { value: 'Meter', label: UoM['Meter'] },
        { value: 'Foot', label: UoM['Foot'] },
        { value: 'Inch', label: UoM['Inch'] }
      ],
      lengthUoM: [
        { value: 'Millimeter', label: UoM['Millimeter'] },
        { value: 'Meter', label: UoM['Meter'] },
        { value: 'Foot', label: UoM['Foot'] },
        { value: 'Inch', label: UoM['Inch'] }
      ],
      volumeUoM:
        projectCountry === COUNTRY.USA
          ? [
              { value: 'CubicYard', label: UoM['CubicYard'] },
              { value: 'UsGallon', label: UoM['UsGallon'] },
              { value: 'CubicFoot', label: UoM['CubicFoot'] },
              { value: 'BoardFoot', label: UoM['BoardFoot'] }
            ]
          : [
              { value: 'CubicMeter', label: UoM['CubicMeter'] },
              { value: 'Liter', label: UoM['Liter'] },
              { value: 'ImperialGallon', label: UoM['ImperialGallon'] }
            ],
      weightUoM:
        projectCountry === COUNTRY.USA
          ? [
              { value: 'Pound', label: UoM['Pound'] },
              { value: 'ShortTon', label: UoM['ShortTon'] }
            ]
          : [
              { value: 'Kilogram', label: UoM['Kilogram'] },
              { value: 'Tonne', label: UoM['Tonne'] }
            ]
    }),
    [UoM, projectCountry]
  )

  const DateFormatFull = (): Intl.DateTimeFormatOptions => {
    return {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: 'numeric',
      minute: 'numeric'
    }
  }

  const referenceProductGroups: any[] = useMemo(() => {
    if (!allProductGroups.data || !allProductGroups.isFetched) {
      return []
    }

    const filteredGroups = allProductGroups.data.map(rpg => {
      return {
        value: `${rpg.productGroupId}:${rpg.name}`,
        label: rpg.name,
        className: rpg.name.replace(/[\s&()]+/g, '-').toLowerCase(),
        title: rpg.description
      }
    })

    const deDupedGroups = [
      ...new Map(filteredGroups.map(v => [v.value, v])).values()
    ]

    return deDupedGroups
  }, [allProductGroups])

  const certOptions = useMemo(() => {
    if (!DeliveryProductCertOptions) {
      return []
    }
    return DeliveryProductCertOptions.map(option => {
      return {
        value: option,
        label: option
      }
    })
  }, [])

  const handleClick = useCallback(
    (record: any, field: string, index: number) => {
      // onClick close modal and move focus to related field
      onClose()
      // Make sure Data view is set to enriched
      if (dataView !== ENRICHED) {
        performance.mark('raw-enriched-tab-switch', {
          detail: { context: 'summary table', destination: ENRICHED }
        })
        setDataView(ENRICHED)
      }

      // Make sure measurement and lineItem is expanded
      setTimeout(() => {
        // Sometimes the input element has a parent with the same name, so we need to get the last one
        const elements = Array.from(
          document.querySelectorAll(`[name="uniquePrefix[${index}].${field}"]`)
        ) as HTMLInputElement[]
        const element = elements
          ? (elements[elements.length - 1] as HTMLInputElement)
          : null

        if (field === 'referenceProductId') {
          const selectElement = document.querySelector(
            `[name="uniquePrefix[${index}].${field}"] .ant-select-selector`
          ) as HTMLInputElement

          if (selectElement) {
            selectElement.click()
          }
        } else if (element) {
          const closestDetails = element.closest('details')
          if (closestDetails) {
            // check if has 2nd parent details component, if so open this one first
            const secondLevelDetails =
              closestDetails.parentElement?.closest('details')
            if (secondLevelDetails) {
              secondLevelDetails.open = true
            }

            closestDetails.open = true
            closestDetails.scrollIntoView()
          }

          element.focus()
        }
      }, 100)
    },
    [dataView, onClose, setDataView]
  )

  // Check if there are any confidence or validation messages for a given field and return className where appropriate
  const checkConfidenceAndValidation = useCallback(
    (id: string, field: string) => {
      if (
        !enrichedConfidenceAndValidation ||
        !enrichedConfidenceAndValidation[id] ||
        !enrichedConfidenceAndValidation[id][field]
      ) {
        return ''
      }
      // If confidence and Validations for this field contains one or more validation of
      // type ERROR, ATTENTION or WARNING return error-no-icon className,
      // otherwise return empty string
      const validations: ValidationType[] =
        enrichedConfidenceAndValidation[id][field].validations

      if (validations.length > 0) {
        if (
          validations.some(v =>
            [
              CONFIDENCE_AND_VALIDATION_TYPES.ERROR,
              CONFIDENCE_AND_VALIDATION_TYPES.ATTENTION,
              CONFIDENCE_AND_VALIDATION_TYPES.WARNING
            ].includes(v.type)
          )
        ) {
          return 'error-no-icon'
        }
        return ''
      }

      // Else use confidence value to determine if should return error-no-icon className or empty string
      const confidence: number =
        enrichedConfidenceAndValidation[id][field].confidence
      if (
        confidence &&
        CONFIDENCE_BASED_TYPE(confidence) &&
        CONFIDENCE_BASED_TYPE(confidence) ===
          CONFIDENCE_AND_VALIDATION_TYPES.HIGH_CONFIDENCE
      ) {
        return `contains-validation-${CONFIDENCE_BASED_TYPE(confidence).toLowerCase()}`
      }

      return ''
    },
    [enrichedConfidenceAndValidation]
  )

  const handleSelectClear = useCallback(
    (fieldName: string, index: number) => {
      // For easier saving, Make sure Data view is set to enriched and all items are expanded
      if (dataView !== ENRICHED) {
        performance.mark('raw-enriched-tab-switch', {
          detail: { context: 'summary table', destination: ENRICHED }
        })
        setDataView(ENRICHED)
      }

      const element = document.querySelector(
        `[name="uniquePrefix[${index}].${fieldName}"] .ant-select-clear`
      ) as HTMLInputElement
      if (element) {
        // Create a new mousedown event
        const event = new MouseEvent('mousedown', {
          view: window,
          bubbles: true,
          cancelable: true
        })

        // Dispatch the event
        element.dispatchEvent(event)
      }
    },
    [dataView, setDataView]
  )

  const handleSelectClearAll = useCallback(
    (fieldName: string) => {
      // For easier saving, Make sure Data view is set to enriched and all items are expanded
      if (dataView !== ENRICHED) {
        performance.mark('raw-enriched-tab-switch', {
          detail: { context: 'summary table', destination: ENRICHED }
        })
        setDataView(ENRICHED)
      }

      setTimeout(() => {
        const elements = document.querySelectorAll(
          `#line-item-summary-table [id^='${fieldName}-']`
        )

        elements.forEach((element: any) => {
          // Skip if field has high confidence
          if (fieldName === 'productGroups') {
            const index = parseInt(element.id.split('-')[1])
            const item = items[index]
            if (
              item &&
              checkConfidenceAndValidation(item.id, 'productGroups') ===
                'contains-validation-high-confidence'
            ) {
              return
            }
          }

          // Create a new mousedown event
          const event = new MouseEvent('mousedown', {
            view: window,
            bubbles: true,
            cancelable: true
          })
          // Dispatch the event
          element
            .closest('.ant-select')
            .querySelector('.ant-select-clear')
            ?.dispatchEvent(event)
        })
      }, 10)
    },
    [dataView, setDataView, items, checkConfidenceAndValidation]
  )

  const handleSelectChange = useCallback(
    (value: string, option: any, fieldName: string, index: number) => {
      // For easier saving, Make sure Data view is set to enriched and all items are expanded
      if (dataView !== ENRICHED) {
        performance.mark('raw-enriched-tab-switch', {
          detail: { context: 'summary table', destination: ENRICHED }
        })
        setDataView(ENRICHED)
      }

      setTimeout(() => {
        const element = document.querySelector(
          `[name="uniquePrefix[${index}].${fieldName}"] input`
        ) as HTMLInputElement

        if (element) {
          // Create a mousedown event
          const mdEvent = new MouseEvent('mousedown', {
            view: window,
            bubbles: true,
            cancelable: true
          })

          // Create a focus event
          const focusEvent = new FocusEvent('focus', {
            view: window,
            bubbles: true,
            cancelable: true
          })
          // Dispatch the events
          element.dispatchEvent(mdEvent)
          element.dispatchEvent(focusEvent)

          setTimeout(() => {
            // Find Element from shared Component
            const optionElement = option.className
              ? (document.querySelector(
                  `.ant-select-item-option.${option.className}[id^="${element.id}"]`
                ) as HTMLInputElement)
              : (document.querySelector(
                  `.ant-select-item-option[title="${option.label}"][id^="${element.id}"]`
                ) as HTMLInputElement)

            optionElement?.click()

            // Find Element from repeated Component
            if (!optionElement) {
              const optionList = document.querySelector(`#${element.id}_list`)

              const optionElement = option.className
                ? (optionList?.parentElement?.querySelector(
                    `.ant-select-item-option.${option.className}`
                  ) as HTMLInputElement)
                : (optionList?.parentElement?.querySelector(
                    `.ant-select-item-option[title="${option.label}"]`
                  ) as HTMLInputElement)

              optionElement?.click()
            }
          }, 0)
        }
      }, 0)
    },
    [dataView, setDataView]
  )

  const handleSelectChangeAll = useCallback(
    (value: string, option: any, fieldName: string) => {
      // For easier saving, Make sure Data view is set to enriched and all items are expanded
      if (dataView !== ENRICHED) {
        performance.mark('raw-enriched-tab-switch', {
          detail: { context: 'summary table', destination: ENRICHED }
        })
        setDataView(ENRICHED)
      }

      const elements = document.querySelectorAll(
        `#line-item-summary-table [id^='${fieldName}-']`
      )

      // Create a new mousedown event
      const mdEvent = new MouseEvent('mousedown', {
        view: window,
        bubbles: true,
        cancelable: true
      })

      // Create a focus event
      const focusEvent = new FocusEvent('focus', {
        view: window,
        bubbles: true,
        cancelable: true
      })

      elements.forEach((element: any) => {
        // Skip if field has high confidence
        if (fieldName === 'productGroups') {
          const index = parseInt(element.id.split('-')[1])
          const item = items[index]
          if (
            item &&
            checkConfidenceAndValidation(item.id, 'productGroups') ===
              'contains-validation-high-confidence'
          ) {
            return
          }
        }

        // Dispatch the event
        element.dispatchEvent(mdEvent)
        element.dispatchEvent(focusEvent)

        const elementId = element.id
        setTimeout(() => {
          const optionGroup = document.querySelector(`#${elementId}_list`)

          const optionElement = option.className
            ? (optionGroup?.parentElement?.querySelector(
                `.ant-select-item-option.${option.className}`
              ) as HTMLInputElement)
            : (optionGroup?.parentElement?.querySelector(
                `.ant-select-item-option[title="${option.label}"]`
              ) as HTMLInputElement)

          optionElement?.scrollIntoView()
          optionElement?.click()
        }, 0)
      })
    },
    [dataView, setDataView, items, checkConfidenceAndValidation]
  )

  const handleCheckboxChange = useCallback(
    (e: any) => {
      // For easier saving, Make sure Data view is set to enriched and all items are expanded
      if (dataView !== ENRICHED) {
        performance.mark('raw-enriched-tab-switch', {
          detail: { context: 'summary table', destination: ENRICHED }
        })
        setDataView(ENRICHED)
      }

      const index = e.target.id.split('-')[1]
      const element = document.querySelector(
        `[name="uniquePrefix[${index}].isHiredOrRented"]`
      ) as HTMLElement
      element?.parentElement?.querySelector('label')?.click()
    },
    [dataView, setDataView]
  )

  const handleCheckboxChangeAll = useCallback(
    (checked: boolean) => {
      // For easier saving, Make sure Data view is set to enriched and all items are expanded
      if (dataView !== ENRICHED) {
        performance.mark('raw-enriched-tab-switch', {
          detail: { context: 'summary table', destination: ENRICHED }
        })
        setDataView(ENRICHED)
      }

      const elements = document.querySelectorAll("[id ^= 'isHiredOrRented']")
      elements.forEach((element: any) => {
        if (element.checked !== checked) {
          element.click()
        }
      })
    },
    [dataView, setDataView]
  )

  const handleUoMChange = useCallback(
    (value: string, option: any, fieldName: string, index: number) => {
      // For easier saving, Make sure Data view is set to enriched and all items are expanded
      if (dataView !== ENRICHED) {
        performance.mark('raw-enriched-tab-switch', {
          detail: { context: 'summary table', destination: ENRICHED }
        })
        setDataView(ENRICHED)
      }

      setTimeout(() => {
        const element = document.getElementById(
          `uniquePrefix[${index}].${fieldName}`
        )

        if (element) {
          // Create a Click event
          const mdEvent = new MouseEvent('click', {
            bubbles: true,
            cancelable: true
          })

          // Dispatch the events
          element.dispatchEvent(mdEvent)

          setTimeout(() => {
            // If new UoM is not the same option.label recall this function
            if (element.innerText !== option.label) {
              handleUoMChange(value, option, fieldName, index)
            }
          }, 0)
        }
      }, 0)
    },
    [dataView, setDataView]
  )

  const lookupProductNameFunction = productName => {
    const encodedString = encodeURIComponent(productName)

    window.open(`https://www.google.com/search?q=${encodedString}`, '_blank')
  }

  const lookupFunction = useCallback(
    record => {
      const pgString = referenceProductGroups.map(rpg => rpg.label).join(', ')
      const supplier = (
        document.getElementsByName('raw.supplierName')[0] as HTMLInputElement
      )?.value
      const productName = record.name

      const string = `Construction material classification for ${productName} from ${supplier} - determine which of these product groups it belongs to (${pgString})`
      const encodedString = encodeURIComponent(string)

      window.open(`https://www.google.com/search?q=${encodedString}`, '_blank')
    },
    [referenceProductGroups]
  )

  const getProductGroupOptions = useCallback(
    (record: any) => {
      if (
        record.productGroups &&
        !referenceProductGroups.some(g => g.value === record.productGroups)
      ) {
        const name = record.productGroups.split(':')[1]
        return [
          { value: record.productGroups, label: `${name}: Out of Taxonomy` },
          ...referenceProductGroups
        ]
      }
      return referenceProductGroups
    },
    [referenceProductGroups]
  )

  const columns: ColumnsType = useMemo(
    () =>
      moduleName === 'Delivery'
        ? [
            {
              title: '#',
              dataIndex: '',
              key: 'index',
              render: (text: any, record: any, index: number) => index + 1,
              align: 'right',
              width: '2.4375rem'
            },
            {
              title: 'Product Name',
              dataIndex: 'name',
              key: 'name',
              render: (text: any, record: any, index: number) =>
                text ? (
                  <ProductNameContainer>
                    <EllipsisTwoLines
                      onClick={() => handleClick(record, 'name', index)}
                      className={
                        Validation.CheckProductName(text).length > 0
                          ? 'warning-no-icon'
                          : ''
                      }
                    >
                      <Tooltip
                        title={
                          <TranslationTextForTooltip
                            cleanedText={text}
                            projectLocale={projectLocale}
                          />
                        }
                      >
                        {text}
                      </Tooltip>
                    </EllipsisTwoLines>
                    <Tooltip title={'Google Product Name'}>
                      <Icons.SearchOutlined
                        onClick={() => lookupProductNameFunction(text)}
                      />
                    </Tooltip>
                  </ProductNameContainer>
                ) : (
                  ''
                )
            },
            {
              title: 'Quantity',
              dataIndex: 'quantity',
              key: 'quantity',
              width: '4.5rem',
              render: (text: number, record: any, index: number) =>
                text ? (
                  <div
                    onClick={() => handleClick(record, 'quantity', index)}
                    className={
                      Validation.CheckQuantity(text.toString()).length > 0
                        ? 'warning-no-icon'
                        : ''
                    }
                  >
                    {text}
                  </div>
                ) : (
                  ''
                )
            },
            {
              title: (
                <Tooltip
                  styles={{ root: { width: '10rem' } }}
                  title={
                    enrichedDisabled ? (
                      ''
                    ) : (
                      <Select
                        placeholder="Set all units"
                        optionFilterProp="label"
                        options={UoMOptions.heightUoM}
                        disabled={enrichedDisabled}
                        onSelect={(value, option) =>
                          handleSelectChangeAll(
                            value as string,
                            option,
                            'heightUoM'
                          )
                        }
                        style={{ width: '100%' }}
                        listHeight={352}
                      />
                    )
                  }
                >
                  Height
                </Tooltip>
              ),
              dataIndex: 'height',
              key: 'height',
              render: (text: any, record: any, index: number) =>
                isImperial ? (
                  `${record.heightYards ? `${record.heightYards} yd ` : ''}${record.heightFeet ? `${record.heightFeet} ft ` : ''}${record.heightInches ? `${record.heightInches} in` : ''}`
                ) : text ? (
                  <ValueWithUoM>
                    <div
                      onClick={() => handleClick(record, 'height', index)}
                      className={
                        Validation.CheckHeightLengthWidth(
                          text,
                          MAX_MEASUREMENT_SIZES.height,
                          record.heightUoM,
                          record.productGroups
                            ? record.productGroups.split(':')[1]
                            : null,
                          'height'
                        ).length > 0
                          ? 'warning-no-icon'
                          : ''
                      }
                    >
                      {text}
                    </div>
                    <Select
                      placeholder="Select Unit"
                      defaultValue={record.heightUoM}
                      optionFilterProp="label"
                      options={UoMOptions.heightUoM}
                      id={`heightUoM-${index}`}
                      disabled={enrichedDisabled}
                      onSelect={(value, option) =>
                        handleUoMChange(value, option, 'heightUoM', index)
                      }
                      size="small"
                      style={{ width: '42px' }}
                      suffixIcon={null}
                      popupMatchSelectWidth={false}
                    />
                  </ValueWithUoM>
                ) : (
                  ''
                )
            },
            {
              title: (
                <Tooltip
                  styles={{ root: { width: '10rem' } }}
                  title={
                    enrichedDisabled ? (
                      ''
                    ) : (
                      <Select
                        placeholder="Set all units"
                        optionFilterProp="label"
                        options={UoMOptions.widthUoM}
                        disabled={enrichedDisabled}
                        onSelect={(value, option) =>
                          handleSelectChangeAll(
                            value as string,
                            option,
                            'widthUoM'
                          )
                        }
                        style={{ width: '100%' }}
                        listHeight={352}
                      />
                    )
                  }
                >
                  Width
                </Tooltip>
              ),
              dataIndex: 'width',
              key: 'width',
              render: (text: any, record: any, index: number) =>
                isImperial ? (
                  `${record.widthYards ? `${record.widthYards} yd ` : ''}${record.widthFeet ? `${record.widthFeet} ft ` : ''}${record.widthInches ? `${record.widthInches} in` : ''}`
                ) : text ? (
                  <ValueWithUoM>
                    <div
                      onClick={() => handleClick(record, 'width', index)}
                      className={
                        Validation.CheckHeightLengthWidth(
                          text,
                          record.name.toLowerCase().includes('rebar')
                            ? MAX_MEASUREMENT_SIZES.rebarWidth
                            : MAX_MEASUREMENT_SIZES.width,
                          record.widthUoM,
                          record.productGroups
                            ? record.productGroups.split(':')[1]
                            : null,
                          'width'
                        ).length > 0
                          ? 'warning-no-icon'
                          : ''
                      }
                    >
                      {text}
                    </div>
                    <Select
                      placeholder="Select Unit"
                      defaultValue={record.widthUoM}
                      optionFilterProp="label"
                      options={UoMOptions.widthUoM}
                      id={`widthUoM-${index}`}
                      disabled={enrichedDisabled}
                      onSelect={(value, option) =>
                        handleUoMChange(value, option, 'widthUoM', index)
                      }
                      size="small"
                      style={{ width: '42px' }}
                      suffixIcon={null}
                      popupMatchSelectWidth={false}
                    />
                  </ValueWithUoM>
                ) : (
                  ''
                )
            },
            {
              title: (
                <Tooltip
                  styles={{ root: { width: '10rem' } }}
                  title={
                    enrichedDisabled ? (
                      ''
                    ) : (
                      <Select
                        placeholder="Set all units"
                        optionFilterProp="label"
                        options={UoMOptions.lengthUoM}
                        disabled={enrichedDisabled}
                        onSelect={(value, option) =>
                          handleSelectChangeAll(
                            value as string,
                            option,
                            'lengthUoM'
                          )
                        }
                        style={{ width: '100%' }}
                        listHeight={352}
                      />
                    )
                  }
                >
                  Length
                </Tooltip>
              ),
              dataIndex: 'length',
              key: 'length',
              render: (text: any, record: any, index: number) =>
                isImperial ? (
                  `${record.lengthYards ? `${record.lengthYards} yd ` : ''}${record.lengthFeet ? `${record.lengthFeet} ft ` : ''}${record.lengthInches ? `${record.lengthInches} in` : ''}`
                ) : text ? (
                  <ValueWithUoM>
                    <div
                      onClick={() => handleClick(record, 'length', index)}
                      className={
                        Validation.CheckHeightLengthWidth(
                          text,
                          record.name.toLowerCase().includes('rebar')
                            ? MAX_MEASUREMENT_SIZES.rebarLength
                            : MAX_MEASUREMENT_SIZES.length,
                          record.lengthUoM,
                          record.productGroups
                            ? record.productGroups.split(':')[1]
                            : null,
                          'length'
                        ).length > 0
                          ? 'warning-no-icon'
                          : ''
                      }
                    >
                      {text}
                    </div>
                    <Select
                      placeholder="Select Unit"
                      defaultValue={record.lengthUoM}
                      optionFilterProp="label"
                      options={UoMOptions.lengthUoM}
                      id={`lengthUoM-${index}`}
                      disabled={enrichedDisabled}
                      onSelect={(value, option) =>
                        handleUoMChange(value, option, 'lengthUoM', index)
                      }
                      size="small"
                      style={{ width: '42px' }}
                      suffixIcon={null}
                      popupMatchSelectWidth={false}
                    />
                  </ValueWithUoM>
                ) : (
                  ''
                )
            },
            {
              title: (
                <Tooltip
                  styles={{ root: { width: '10rem' } }}
                  title={
                    enrichedDisabled ? (
                      ''
                    ) : (
                      <Select
                        placeholder="Set all units"
                        optionFilterProp="label"
                        options={UoMOptions.volumeUoM}
                        disabled={enrichedDisabled}
                        onSelect={(value, option) =>
                          handleSelectChangeAll(
                            value as string,
                            option,
                            'givenVolumeUoM'
                          )
                        }
                        style={{ width: '100%' }}
                        listHeight={352}
                      />
                    )
                  }
                >
                  Given Volume
                </Tooltip>
              ),
              dataIndex: 'givenVolume',
              key: 'givenVolume',
              width: '6.5rem',
              render: (text: any, record: any, index: number) =>
                text ? (
                  <ValueWithUoM>
                    <div
                      onClick={() => handleClick(record, 'givenVolume', index)}
                      className={
                        Validation.CheckVolume(
                          text,
                          record.givenVolumeUoM,
                          record.productGroups
                            ? record.productGroups.split(':')[1]
                            : null
                        ).length > 0
                          ? 'warning-no-icon'
                          : ''
                      }
                    >
                      {text}
                    </div>
                    <Select
                      placeholder="Select Unit"
                      defaultValue={record.givenVolumeUoM}
                      optionFilterProp="label"
                      options={UoMOptions.volumeUoM}
                      id={`givenVolumeUoM-${index}`}
                      disabled={enrichedDisabled}
                      onSelect={(value, option) =>
                        handleUoMChange(value, option, 'givenVolumeUoM', index)
                      }
                      size="small"
                      style={{ width: '42px' }}
                      suffixIcon={null}
                      popupMatchSelectWidth={false}
                    />
                  </ValueWithUoM>
                ) : (
                  <NoValue>--</NoValue>
                )
            },
            {
              title: 'Est. Volume',
              dataIndex: 'calculatedVolume',
              key: 'calculatedVolume',
              width: '6rem',
              render: (text: any) =>
                text ? (
                  `${_.round(text, isImperial ? 3 : 5)} ${UoM['CubicMeter']}`
                ) : (
                  <NoValue>--</NoValue>
                )
            },
            {
              title: (
                <Tooltip
                  styles={{ root: { width: '10rem' } }}
                  title={
                    enrichedDisabled ? (
                      ''
                    ) : (
                      <Select
                        placeholder="Set all units"
                        optionFilterProp="label"
                        options={UoMOptions.weightUoM}
                        disabled={enrichedDisabled}
                        onSelect={(value, option) =>
                          handleSelectChangeAll(
                            value as string,
                            option,
                            'weightUoM'
                          )
                        }
                        style={{ width: '100%' }}
                        listHeight={352}
                      />
                    )
                  }
                >
                  Given Weight
                </Tooltip>
              ),
              dataIndex: 'weight',
              key: 'weight',
              width: '6.5rem',
              render: (text: any, record: any, index: number) =>
                text ? (
                  <ValueWithUoM>
                    <div
                      onClick={() => handleClick(record, 'weight', index)}
                      className={
                        Validation.CheckWeight(
                          text,
                          record.weightUoM,
                          record.productGroups
                            ? record.productGroups.split(':')[1]
                            : null
                        ).length > 0
                          ? 'warning-no-icon'
                          : checkConfidenceAndValidation(record.id, 'weight')
                      }
                    >
                      {text}
                    </div>
                    <Select
                      placeholder="Select Unit"
                      defaultValue={record.weightUoM}
                      optionFilterProp="label"
                      options={UoMOptions.weightUoM}
                      id={`weightUoM-${index}`}
                      disabled={enrichedDisabled}
                      onSelect={(value, option) =>
                        handleUoMChange(value, option, 'weightUoM', index)
                      }
                      size="small"
                      style={{ width: '42px' }}
                      suffixIcon={null}
                      popupMatchSelectWidth={false}
                    />
                  </ValueWithUoM>
                ) : (
                  <NoValue>--</NoValue>
                )
            },
            {
              title: 'Est. Weight',
              dataIndex: 'estimatedWeight',
              key: 'estimatedWeight',
              width: '6rem',
              render: (text: any) =>
                text ? (
                  <div>{`${text} ${UoM['Tonne']}`}</div>
                ) : (
                  <NoValue>--</NoValue>
                )
            },
            {
              title: (
                <span>
                  Produced At
                  <br />
                  <wbr />/<wbr /> Used At
                </span>
              ),
              dataIndex: 'productionTime',
              key: 'productionTime',
              render: (text: any, record: any, index: number) =>
                text ? (
                  <>
                    <div
                      onClick={() =>
                        handleClick(record, 'productionTime', index)
                      }
                      className={
                        (arrivalDate &&
                          new Date(text) > new Date(arrivalDate)) ||
                        (exitDate && new Date(text) > new Date(exitDate))
                          ? 'warning-no-icon'
                          : ''
                      }
                    >
                      {new Date(text).toLocaleString(
                        isImperial ? 'en-us' : 'en-GB',
                        DateFormatFull()
                      )}
                    </div>
                    <div
                      onClick={() => handleClick(record, 'usedAtTime', index)}
                      className={
                        new Date(record.productionTime) > new Date(text) ||
                        (exitDate &&
                          new Date(record.usedAtTime) > new Date(exitDate))
                          ? 'warning-no-icon'
                          : ''
                      }
                    >
                      {new Date(record.usedAtTime).toLocaleString(
                        isImperial ? 'en-us' : 'en-GB',
                        DateFormatFull()
                      )}
                    </div>
                  </>
                ) : (
                  ''
                )
            },
            {
              title: (
                <Tooltip
                  styles={{ root: { width: '10rem' } }}
                  title={
                    enrichedDisabled ? (
                      ''
                    ) : (
                      <>
                        <Select
                          placeholder="Set all Product Certifications"
                          showSearch
                          optionFilterProp="label"
                          options={certOptions}
                          disabled={enrichedDisabled}
                          onSelect={(value, option) =>
                            handleSelectChangeAll(
                              value as string,
                              option,
                              'givenCertifications'
                            )
                          }
                          style={{ width: '100%' }}
                          listHeight={352}
                        />
                        <TooltipAction
                          onClick={() =>
                            handleSelectClearAll('givenCertifications')
                          }
                        >
                          Clear all
                        </TooltipAction>
                      </>
                    )
                  }
                >
                  Given Certifications
                </Tooltip>
              ),
              dataIndex: 'givenCertifications',
              key: 'givenCertifications',
              render: (text: any, record: any, index: number) => (
                <Select
                  allowClear
                  placeholder="Select Product Certifications"
                  defaultValue={text}
                  showSearch
                  optionFilterProp="label"
                  options={certOptions}
                  id={`givenCertifications-${index}`}
                  disabled={enrichedDisabled}
                  onClear={() =>
                    handleSelectClear('givenCertifications', index)
                  }
                  onSelect={(value, option) =>
                    handleSelectChange(
                      value,
                      option,
                      'givenCertifications',
                      index
                    )
                  }
                  style={{ width: '100%' }}
                  popupMatchSelectWidth={false}
                  listHeight={352}
                />
              )
            },
            {
              title: 'Known Certifications',
              dataIndex: 'referenceProductId',
              key: 'referenceProductId',
              width: 'max-content',
              render: (text: string, record: any, index: number) => (
                <div
                  onClick={() =>
                    handleClick(record, 'referenceProductId', index)
                  }
                >
                  <KnownCertifications
                    id={text}
                    productGroups={record.productGroups}
                  />
                </div>
              )
            },
            {
              title: (
                <div
                  style={
                    isExtendedPGList(referenceProductGroups)
                      ? { border: 'solid 3px hotpink', borderRadius: '2px' }
                      : {}
                  }
                >
                  <Tooltip
                    styles={{
                      root: { width: '10rem' }
                    }}
                    title={
                      enrichedDisabled ? (
                        ''
                      ) : (
                        <>
                          <Select
                            placeholder="Set all Product Group"
                            showSearch
                            optionFilterProp="label"
                            options={referenceProductGroups}
                            disabled={enrichedDisabled}
                            onSelect={(value, option) =>
                              handleSelectChangeAll(
                                value as string,
                                option,
                                'productGroups'
                              )
                            }
                            style={{ width: '100%' }}
                            listHeight={352}
                          />
                          <TooltipAction
                            onClick={() =>
                              handleSelectClearAll('productGroups')
                            }
                          >
                            Clear all
                          </TooltipAction>
                        </>
                      )
                    }
                  >
                    Product Groups
                  </Tooltip>
                </div>
              ),
              dataIndex: 'productGroups',
              key: 'productGroups',
              render: (text: any, record: any, index: number) => (
                <div style={{ width: '100%', minWidth: 0 }}>
                  <Select
                    allowClear
                    placeholder="Select Product Group"
                    defaultValue={text}
                    showSearch
                    optionFilterProp="label"
                    options={getProductGroupOptions(record)}
                    id={`productGroups-${index}`}
                    disabled={enrichedDisabled}
                    onClear={() => handleSelectClear('productGroups', index)}
                    onSelect={(value, option) =>
                      handleSelectChange(value, option, 'productGroups', index)
                    }
                    popupMatchSelectWidth={false}
                    listHeight={352}
                    lookupFunction={() => lookupFunction(record)}
                    lookupTooltipText="Ask Google to suggest a Product Group"
                    style={{ flex: 1, minWidth: 0 }}
                    className={checkConfidenceAndValidation(
                      record.id,
                      'productGroups'
                    )}
                  />
                </div>
              )
            },
            {
              title: (
                <Tooltip
                  title={
                    enrichedDisabled ? (
                      ''
                    ) : (
                      <>
                        <TooltipAction
                          onClick={() => handleCheckboxChangeAll(true)}
                        >
                          Check all
                        </TooltipAction>
                        <TooltipAction
                          onClick={() => handleCheckboxChangeAll(false)}
                        >
                          Uncheck all
                        </TooltipAction>
                      </>
                    )
                  }
                >
                  Hired
                  <wbr />/<wbr />
                  Rented
                </Tooltip>
              ),
              dataIndex: 'isHiredOrRented',
              key: 'isHiredOrRented',
              align: 'center',
              width: '5rem',
              render: (value: boolean, record: any, index: number) => (
                <Checkbox
                  id={`isHiredOrRented-${index}`}
                  defaultChecked={value}
                  onChange={handleCheckboxChange}
                  style={{ paddingBottom: '6px' }}
                  disabled={enrichedDisabled}
                />
              )
            }
          ]
        : [
            {
              title: '#',
              dataIndex: '',
              key: 'index',
              render: (text: any, record: any, index: number) => index + 1,
              align: 'right',
              width: '2.5rem'
            },
            {
              title: 'Container Type',
              dataIndex: 'containerType',
              key: 'containerType'
            },
            {
              title: 'Known Container Type',
              dataIndex: 'knownContainerType',
              key: 'knownContainerType',
              render: (text: any, record: any, index: number) =>
                text ? (
                  <span
                    onClick={() =>
                      handleClick(record, 'knownContainerType', index)
                    }
                  >
                    {text}
                  </span>
                ) : (
                  <div
                    onClick={() =>
                      handleClick(record, 'knownContainerType', index)
                    }
                    className={'warning-no-icon'}
                  >
                    !
                  </div>
                )
            },
            {
              title: 'Waste Description',
              dataIndex: 'wasteDescription',
              key: 'wasteDescription',
              render: (text: any, record: any, index: number) =>
                text ? (
                  <EllipsisTwoLines
                    onClick={() =>
                      handleClick(record, 'wasteDescription', index)
                    }
                  >
                    <Tooltip title={text}>{text}</Tooltip>
                  </EllipsisTwoLines>
                ) : (
                  ''
                )
            },
            {
              title: 'Waste Code',
              dataIndex: 'wasteCode',
              key: 'wasteCode',
              render: (text: any, record: any, index: number) =>
                text ? (
                  <span onClick={() => handleClick(record, 'wasteCode', index)}>
                    {text}
                  </span>
                ) : (
                  ''
                )
            },
            {
              title: (
                <Tooltip
                  styles={{ root: { width: '10rem' } }}
                  title={
                    enrichedDisabled ? (
                      ''
                    ) : (
                      <Select
                        placeholder="Set all units"
                        optionFilterProp="label"
                        options={UoMOptions.volumeUoM}
                        disabled={enrichedDisabled}
                        onSelect={(value, option) =>
                          handleSelectChangeAll(
                            value as string,
                            option,
                            'volumeUoM'
                          )
                        }
                        style={{ width: '100%' }}
                        listHeight={352}
                      />
                    )
                  }
                >
                  Given Volume
                </Tooltip>
              ),
              dataIndex: 'volume',
              key: 'volume',
              render: (text: any, record: any, index: number) =>
                text ? (
                  <ValueWithUoM>
                    <div
                      onClick={() => handleClick(record, 'volume', index)}
                      className={
                        Validation.CheckVolume(text, record.volumeUoM).length >
                        0
                          ? 'warning-no-icon'
                          : ''
                      }
                    >
                      {text}
                    </div>
                    <Select
                      placeholder="Select Unit"
                      defaultValue={record.volumeUoM}
                      optionFilterProp="label"
                      options={UoMOptions.volumeUoM}
                      id={`volumeUoM-${index}`}
                      disabled={enrichedDisabled}
                      onSelect={(value, option) =>
                        handleUoMChange(value, option, 'volumeUoM', index)
                      }
                      size="small"
                      style={{ width: '42px' }}
                      suffixIcon={null}
                      popupMatchSelectWidth={false}
                    />
                  </ValueWithUoM>
                ) : (
                  ''
                )
            },
            {
              title: (
                <Tooltip
                  styles={{ root: { width: '10rem' } }}
                  title={
                    enrichedDisabled ? (
                      ''
                    ) : (
                      <Select
                        placeholder="Set all units"
                        optionFilterProp="label"
                        options={UoMOptions.weightUoM}
                        disabled={enrichedDisabled}
                        onSelect={(value, option) =>
                          handleSelectChangeAll(
                            value as string,
                            option,
                            'weightUoM'
                          )
                        }
                        style={{ width: '100%' }}
                        listHeight={352}
                      />
                    )
                  }
                >
                  Given Weight
                </Tooltip>
              ),
              dataIndex: 'weight',
              key: 'weight',
              render: (text: any, record: any, index: number) =>
                text ? (
                  <ValueWithUoM>
                    <div
                      onClick={() => handleClick(record, 'weight', index)}
                      className={
                        Validation.CheckWeight(text, record.weightUoM).length >
                        0
                          ? 'warning-no-icon'
                          : ''
                      }
                    >
                      {text}
                    </div>
                    <Select
                      placeholder="Select Unit"
                      defaultValue={record.weightUoM}
                      optionFilterProp="label"
                      options={UoMOptions.weightUoM}
                      id={`weightUoM-${index}`}
                      disabled={enrichedDisabled}
                      onSelect={(value, option) =>
                        handleUoMChange(value, option, 'weightUoM', index)
                      }
                      size="small"
                      style={{ width: '42px' }}
                      suffixIcon={null}
                      popupMatchSelectWidth={false}
                    />
                  </ValueWithUoM>
                ) : (
                  ''
                )
            },
            {
              title: 'Quantity',
              dataIndex: 'quantity',
              key: 'quantity',
              width: '6rem',
              render: (text: number, record: any, index: number) =>
                text ? (
                  <div
                    onClick={() => handleClick(record, 'quantity', index)}
                    className={
                      Validation.CheckQuantity(text.toString()).length > 0
                        ? 'warning-no-icon'
                        : ''
                    }
                  >
                    {text}
                  </div>
                ) : (
                  ''
                )
            }
          ],
    [
      moduleName,
      enrichedDisabled,
      UoMOptions.heightUoM,
      UoMOptions.widthUoM,
      UoMOptions.lengthUoM,
      UoMOptions.volumeUoM,
      UoMOptions.weightUoM,
      certOptions,
      referenceProductGroups,
      projectLocale,
      handleClick,
      handleSelectChangeAll,
      isImperial,
      handleUoMChange,
      UoM,
      checkConfidenceAndValidation,
      arrivalDate,
      exitDate,
      handleSelectClearAll,
      handleSelectClear,
      handleSelectChange,
      lookupFunction,
      handleCheckboxChangeAll,
      handleCheckboxChange,
      getProductGroupOptions
    ]
  )

  const rowClassName = record => {
    return record.isDeleted === true || record.isRemoved === true
      ? 'strikethrough'
      : ''
  }

  const toggleMinimize = () => {
    setMinimized(!minimized)
  }

  useGlobalShortcut(Matchers.lineItems.toggleLineItemSummaryMinification, () =>
    toggleMinimize()
  )

  return (
    <Modal
      title={
        <div
          style={{
            width: '100%',
            cursor: 'move',
            display: 'flex',
            justifyContent: 'space-between'
          }}
          onMouseOver={() => {
            if (disabled) {
              setDisabled(false)
            }
          }}
          onMouseOut={() => {
            setDisabled(true)
          }}
          // fix eslintjsx-a11y/mouse-events-have-key-events
          // https://github.com/jsx-eslint/eslint-plugin-jsx-a11y/blob/master/docs/rules/mouse-events-have-key-events.md
          onFocus={() => {}}
          onBlur={() => {}}
          // end
        >
          {moduleName === 'Delivery' ? 'Product Summary' : 'Item Summary'}
          <Tooltip title={minimized ? 'Maximized' : 'Minimize'}>
            <MinimizeButton onClick={toggleMinimize} $minimized={minimized}>
              <Minimize size={sizes.SMALL_MEDIUM} />
            </MinimizeButton>
          </Tooltip>
        </div>
      }
      open={isOpen}
      cancelText="Close"
      okButtonProps={{ style: { display: 'none' } }}
      onCancel={onClose}
      width="calc(100% - 4rem)"
      styles={{
        wrapper: { pointerEvents: 'none' }
      }}
      modalRender={modal => (
        <Draggable
          disabled={disabled}
          bounds={bounds}
          onStart={(event, uiData) => onStart(event, uiData)}
        >
          <div ref={draggleRef}>{modal}</div>
        </Draggable>
      )}
    >
      {!enrichedDisabled && (
        <NotificationDiv className="info">
          <strong>Info:</strong> Showing last saved data. If you have changed an
          enriched field please close this pop-up and save the changes before
          proceeding.
        </NotificationDiv>
      )}
      {enrichedDisabled && (
        <NotificationDiv className="warning">
          <strong>Warning:</strong> Unsaved changes to Raw data detected. Please
          close this pop-up and save the changes before proceeding.
        </NotificationDiv>
      )}
      <Table
        id="line-item-summary-table"
        dataSource={items}
        columns={columns}
        rowKey={(record: any) => record.id}
        pagination={false}
        rowClassName={rowClassName}
        scroll={{ y: minimized ? 'calc(25vh - 50px)' : 'calc(50vh - 100px)' }}
        size="small"
        sticky
      />
    </Modal>
  )
}

const NotificationDiv = styled.div`
  font-size: 0.875rem;
`
const TooltipAction = styled.div`
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  margin: 0.25rem 0;
  border-radius: 0.25rem;
  &:hover {
    background-color: ${colours.TEAL};
  }
`
const MinimizeButton = styled.div<{ $minimized: boolean }>`
  color: ${({ $minimized }) =>
    $minimized ? colours.SUCCESS : 'rgba(0, 0, 0, 0.85)'};
  background-color: ${({ $minimized }) =>
    $minimized ? `${colours.GREEN}44` : 'transparent'};
  cursor: pointer;
  height: 2rem;
  margin: -0.5rem 1.5rem 0 0;
  padding: 5px 9px;
  border-radius: 0.25rem;

  &:hover {
    color: rgba(0, 0, 0, 0.85);
    background-color: rgba(0, 0, 0, 0.06);
  }
`

export const KnownCertifications = ({ id, productGroups }) => {
  const selectedReferenceProductItem = Queries.useReferenceProductItem(id)
  const allCertificates = Queries.useCertificates()

  const loading =
    !selectedReferenceProductItem.isFetched || !allCertificates.isFetched

  const certificates = useMemo(() => {
    if (
      !selectedReferenceProductItem.data ||
      !allCertificates.data ||
      loading
    ) {
      return []
    }

    return allCertificates.data
      .filter(cert =>
        selectedReferenceProductItem.data?.certificateIds?.includes(
          cert.certificateId
        )
      )
      .map(cert => ({
        ...cert,
        status: getCertStatus(cert),
        expirationDate: moment(cert.expirationDate).local().format('DD/MM/YYYY')
      }))
  }, [allCertificates.data, loading, selectedReferenceProductItem.data])

  if (loading) {
    return <span>Loading...</span>
  }

  // if (Guid.Empty === id || (certificates.length === 0 && productGroups)) {
  //   return <div className="warning-no-icon">Not set</div>
  // }

  return (
    <div>
      {certificates.length > 0 &&
        certificates.map(cert => (
          <SummaryTableTag
            key={cert.certificateId}
            color={cert.status?.color}
            title={
              cert.status?.text &&
              `${cert.status?.text}: ${cert.expirationDate}`
            }
          >
            {cert.certificationScheme} {cert.certificationRating}{' '}
            {cert.certificateNumber}
          </SummaryTableTag>
        ))}
    </div>
  )
}

const SummaryTableTag = styled(Tag)`
  white-space: unset;
`
const ProductNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: space-between;
`
const EllipsisTwoLines = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2; /* Limit to 2 lines */
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
`
const NoValue = styled.div`
  color: ${colours.SUSTAINABLE_GREY};
`
const ValueWithUoM = styled.div`
  display: flex;
  gap: 4px;
  align-items: flex-end;
`
